import React, { FC, useEffect, useState } from 'react'
import { Modal, ModalOverlay, ModalBody, ModalContent, ModalCloseButton, Flex, Image, Box, Icon, Text, Input, FormControl, Select, Button, CircularProgress } from '@chakra-ui/react'
import { addDays, addHours, addYears } from 'date-fns';
import { useFormik } from 'formik';
import * as Yup from 'yup'
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { useIntl } from 'react-intl';
import { ApproveListingModal } from './ApproveListingModal';
import { ViewListingModal } from './ViewListingModal';
import { MarketplaceUtils, NFTUtils } from '../../app/wallet';
import { NftDetailType } from '../../models/NftType';
import { useChainId, useSigner, useSwitchChain } from '@thirdweb-dev/react';
import { createListingNftApi } from '../../setup/api/apiListingNft';
import { toast } from 'react-toastify';
import moment from 'moment'
import { MARKETPLACE_CONTRACT_ADDRESS, URL_COLLECTION_DEFAULT, USDT_CONTRACT_ADDRESS, USDT_CURRENCY, chainIds } from '../../constants';


type Props = {
    priceUsd: number
    openModalListForSale: boolean
    onCloseModalListForSale: () => void
    setOpenModalListForSale: React.Dispatch<React.SetStateAction<boolean>>
    network: string
    nftInformation?: NftDetailType
    handleReloadData: () => void

}
type OptionValue = 'custom' | '1h' | '6h' | '1d' | '3d' | '7d' | '1 month' | '3 month' | '1 year'

const CustomInput = React.forwardRef((props, ref: any) => {
    return (
        <Input {...props} color={"#FFFFFF"} backgroundColor={'#FFFFFF0A'} border={'none'} focusBorderColor='#29E9DD' h='48px' flex={'7'} ref={ref} />
    )
})

export const ListForSaleModal: FC<Props> = ({ priceUsd, openModalListForSale, onCloseModalListForSale, network, nftInformation, handleReloadData }) => {
    const intl = useIntl();
    const now = new Date()

    const [openModalApproveListing, setopenModalApproveListing] = useState(false)
    const [openModalViewListing, setopenModalViewListing] = useState(false)
    const onCloseModalApproveListing = () => {
        setopenModalApproveListing(false)
        onCloseModalListForSale()
    }
    const onCloseModalViewListing = () => {
        setopenModalApproveListing(false)
        setopenModalViewListing(false)
        onCloseModalListForSale()
    }

    const filterPassedTime = (time: any) => {
        const currentDate = new Date();
        const selectedDate = new Date(time);

        return currentDate.getTime() < selectedDate.getTime();
    };

    const [selectedDuration, setSelectedDuration] = useState<OptionValue>('1 year');
    const [endDate, setEndDate] = useState<Date | any>(new Date());

    useEffect(() => {
        updateEndDate('1 year');
    }, []);

    const signer = useSigner()
    const updateEndDate = (value: OptionValue): void => {
        const now = new Date();
        let endDate: Date;

        switch (value) {
            case '1h':
                endDate = addHours(now, 1);
                break;
            case '6h':
                endDate = addHours(now, 6);
                break;
            case '1d':
                endDate = addDays(now, 1);
                break;
            case '3d':
                endDate = addDays(now, 3);
                break;
            case '7d':
                endDate = addDays(now, 7);
                break;
            case '1 month':
                endDate = addDays(now, 30);
                break;
            case '3 month':
                endDate = addDays(now, 90);
                break;
            case '1 year':
                endDate = addYears(now, 1);
                break;
            default:
                endDate = now;
                break;
        }

        setEndDate(endDate);
        setSelectedDuration(value);
    };

    const handleDurationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        updateEndDate(e.target.value as OptionValue);
    };

    const handleDateChange = (date: Date) => {
        setEndDate(date);
        setSelectedDuration('custom');
    };

    type Init = {
        price?: number | null
        endTime?: any
    }

    const initialValues: Init = {
        price: 0,
        endTime: endDate
    }

    const validationSchema = Yup.object().shape({
        price: Yup.number().min(0).required().label(intl.formatMessage({ id: 'NFT.SALE.PRICE' })),
        endTime: Yup.string().required()
    })

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitting(true)
        }
    })

    const chain = useChainId()
    const switchChain = useSwitchChain()

    const handleLogicListingNft = async () => {
        if (!formik?.values?.price) return
        try {
            const startDate = moment(new Date())
            formik?.setSubmitting(true)
            //check isApproved

            const isApproved = await NFTUtils?.getApproved(network, nftInformation?.contractAddress as string, Number(nftInformation?.tokenId))

            //switchChain

            if (chainIds[network] !== chain) {
                await switchChain(chainIds[network])
            }

            let statusApproval
            if (isApproved !== MARKETPLACE_CONTRACT_ADDRESS) {
                setopenModalApproveListing(true)
                statusApproval = await NFTUtils?.approve(signer as any, nftInformation?.contractAddress as string, MARKETPLACE_CONTRACT_ADDRESS, Number(nftInformation?.tokenId))
                if (!statusApproval) {
                    setopenModalApproveListing(false)
                    onCloseModalListForSale()
                    toast?.error(intl.formatMessage({ id: 'WALLET.REJECTED' }), { closeButton: false, })
                    return
                }
            }

            const result = await MarketplaceUtils?.createListing(
                signer as any,
                MARKETPLACE_CONTRACT_ADDRESS,
                {
                    "assetContractAddress": nftInformation?.contractAddress as string,
                    "tokenId": nftInformation?.tokenId as string,
                    "quantity": 1,
                    "currencyContractAddress": USDT_CONTRACT_ADDRESS,
                    "pricePerToken": formik?.values?.price,
                    "startTimestamp": startDate?.unix(),
                    "endTimestamp": Math.floor(endDate / 1000) + 3600,
                    "isReservedListing": false
                }
            )

            if (!result?.receipt.status) {
                onCloseModalListForSale()
                toast?.error(intl.formatMessage({ id: 'WALLET.REJECTED' }), { closeButton: false, })
                return
            }

            if (result?.receipt.status) {
                //save api
                await createListingNftApi({
                    network: network,
                    contractAddress: nftInformation?.contractAddress,
                    tokenId: nftInformation?.tokenId,
                    setting: {
                        price: Number(formik?.values?.price),
                        currency: USDT_CURRENCY,
                        currencyAddress: USDT_CONTRACT_ADDRESS,
                        listingType: 'direct',
                        quantity: 1,
                        startTime: startDate?.toISOString(),
                        endTime: moment(endDate)?.toISOString(),
                        listingId: result?.id?.toNumber(),
                    }
                })
                toast?.success(intl.formatMessage({ id: 'NFT.LISTING.SUCCESS' }), { closeButton: false, })
                setopenModalViewListing(true)
                setopenModalApproveListing(false)
                handleReloadData()

            } else {
                toast?.error('error')
            }

        } catch (error: any) {

            setopenModalViewListing(false)
            setopenModalApproveListing(false)
            onCloseModalListForSale()
            if (error?.reason === 'ERC721: approval to current owner') {
                toast?.error(intl.formatMessage({ id: 'ERROR.ERC721.APPROVAL_TO_CURRENT_OWNER' }))
                return
            } else {
                toast?.error(error?.reason || 'unknown error')
                return
            }
        }

    }

    const [fee, setFee] = useState<number>(0)
    useEffect(() => {
        if (!nftInformation) return
        (async () => {
            const f = await MarketplaceUtils?.getPlatformFeeInfo(nftInformation?.collection?.network, MARKETPLACE_CONTRACT_ADDRESS)
            setFee(f?.[1])
        })()

    }, [nftInformation])
    return (
        <>
            {!openModalApproveListing && !openModalViewListing &&
                <Modal isCentered size={'xl'} isOpen={openModalListForSale} onClose={onCloseModalListForSale}>
                    <ModalOverlay />
                    <ModalContent alignItems={'center'} bg={'#131619'}>
                        <Flex px={'32px'} py={'20px'} w={'100%'} align={'center'} justify={'space-between'}>
                            <Text fontSize={'20px'} fontWeight={700} align={'start'}>{intl.formatMessage({ id: 'NFT.SALE.TITLE' })}</Text>
                            <ModalCloseButton />
                        </Flex>
                        <ModalBody px={'32px'} w={'100%'}>
                            <Flex flexWrap={'wrap'} mb={{ base: '24px', lg: '32px' }} justify={{ lg: 'space-between', base: 'center' }} bg={'#FFFFFF0A'} p={{ base: '8px', lg: '8px' }}>
                                <Flex overflow={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} w={{ lg: '60%', base: '100%' }} justify={'start'} >
                                    <Image
                                        borderRadius={'6px'}
                                        h={{ base: '80px', lg: '80px' }}
                                        w={{ base: '80px', lg: '80px' }}
                                        crossOrigin="anonymous"
                                        objectFit={'contain'}
                                        src={nftInformation?.image ?? URL_COLLECTION_DEFAULT}
                                    />
                                    <Flex overflow={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} align={'center'} ml={{ lg: '16px', base: '12px' }} flexWrap={'wrap'}>
                                        <Box overflow={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} flexWrap={'wrap'} display={'flex'} alignItems={'center'} >
                                            <Text overflow={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} w={'100%'} fontSize={{ lg: '18px', base: '16px' }} fontWeight={700} title={nftInformation?.name || `#${nftInformation?.tokenId}`}>{nftInformation?.name || `#${nftInformation?.tokenId}`}</Text>
                                            <Flex overflow={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} align={'center'} justify={'start'} w={'100%'}>
                                                <Text overflow={'hidden'} textOverflow={'ellipsis'} whiteSpace={'nowrap'} title={nftInformation?.collection?.name} fontWeight={700} fontSize={'16px'} color={'#808489'} >{nftInformation?.collection?.name}</Text>
                                                <Box display={'flex'} justifyContent={'center'} alignItems={'center'} ml={'8px'}>
                                                    <Icon h={'14px'} w={'14px'} viewBox='0 0 14 14' fill="none" color='white'>
                                                        <path d="M14.1163 6.42625C13.8806 6.18 13.6369 5.92625 13.545 5.70312C13.46 5.49875 13.455 5.16 13.45 4.83187C13.4406 4.22187 13.4306 3.53062 12.95 3.05C12.4694 2.56937 11.7781 2.55937 11.1681 2.55C10.84 2.545 10.5013 2.54 10.2969 2.455C10.0744 2.36312 9.82 2.11937 9.57375 1.88375C9.1425 1.46937 8.6525 1 8 1C7.3475 1 6.85812 1.46937 6.42625 1.88375C6.18 2.11937 5.92625 2.36312 5.70312 2.455C5.5 2.54 5.16 2.545 4.83187 2.55C4.22187 2.55937 3.53062 2.56937 3.05 3.05C2.56937 3.53062 2.5625 4.22187 2.55 4.83187C2.545 5.16 2.54 5.49875 2.455 5.70312C2.36312 5.92562 2.11937 6.18 1.88375 6.42625C1.46937 6.8575 1 7.3475 1 8C1 8.6525 1.46937 9.14187 1.88375 9.57375C2.11937 9.82 2.36312 10.0737 2.455 10.2969C2.54 10.5013 2.545 10.84 2.55 11.1681C2.55937 11.7781 2.56937 12.4694 3.05 12.95C3.53062 13.4306 4.22187 13.4406 4.83187 13.45C5.16 13.455 5.49875 13.46 5.70312 13.545C5.92562 13.6369 6.18 13.8806 6.42625 14.1163C6.8575 14.5306 7.3475 15 8 15C8.6525 15 9.14187 14.5306 9.57375 14.1163C9.82 13.8806 10.0737 13.6369 10.2969 13.545C10.5013 13.46 10.84 13.455 11.1681 13.45C11.7781 13.4406 12.4694 13.4306 12.95 12.95C13.4306 12.4694 13.4406 11.7781 13.45 11.1681C13.455 10.84 13.46 10.5013 13.545 10.2969C13.6369 10.0744 13.8806 9.82 14.1163 9.57375C14.5306 9.1425 15 8.6525 15 8C15 7.3475 14.5306 6.85812 14.1163 6.42625ZM10.8538 6.85375L7.35375 10.3538C7.30731 10.4002 7.25217 10.4371 7.19147 10.4623C7.13077 10.4874 7.06571 10.5004 7 10.5004C6.93429 10.5004 6.86923 10.4874 6.80853 10.4623C6.74783 10.4371 6.69269 10.4002 6.64625 10.3538L5.14625 8.85375C5.05243 8.75993 4.99972 8.63268 4.99972 8.5C4.99972 8.36732 5.05243 8.24007 5.14625 8.14625C5.24007 8.05243 5.36732 7.99972 5.5 7.99972C5.63268 7.99972 5.75993 8.05243 5.85375 8.14625L7 9.29313L10.1462 6.14625C10.1927 6.09979 10.2479 6.06294 10.3086 6.0378C10.3692 6.01266 10.4343 5.99972 10.5 5.99972C10.5657 5.99972 10.6308 6.01266 10.6914 6.0378C10.7521 6.06294 10.8073 6.09979 10.8538 6.14625C10.9002 6.1927 10.9371 6.24786 10.9622 6.30855C10.9873 6.36925 11.0003 6.4343 11.0003 6.5C11.0003 6.5657 10.9873 6.63075 10.9622 6.69145C10.9371 6.75214 10.9002 6.8073 10.8538 6.85375Z" fill="#2FB551" />
                                                    </Icon>
                                                </Box>
                                            </Flex>
                                        </Box>
                                    </Flex>
                                </Flex>
                                <Flex p={{ lg: 0, base: '16px' }} w={{ lg: '40%', base: '100%' }} alignItems={'center'}>
                                    <Box display={'flex'} flexWrap={'wrap'} justifyContent={{ lg: 'end', base: 'center' }} alignItems={'center'}>
                                        <Box color={'#808489'} fontSize={'14px'} fontWeight={700} textAlign={{ lg: 'end', base: 'center' }} w={'100%'}>{intl.formatMessage({ id: 'NFT.SALE.PRICE.LISTING' })}</Box>
                                        <Flex flexWrap={'wrap'} align={'center'}>
                                            <Box fontWeight={700} fontSize={{ base: '16px', lg: (formik?.values?.price as number).toString()?.length > 5 ? '16px' : '18px' }}>{Number(formik?.values?.price)} {USDT_CURRENCY}</Box>
                                            {/* <Box color={'#808489'} ml={'8px'} fontWeight={400} fontSize={{ base: '16px', lg: '16px' }}>{formik?.values?.price !== 0 && (priceUsd * (formik?.values?.price || 0)) < 0.01 ? '<0.01' : (priceUsd * (formik?.values?.price || 0))?.toFixed(2)} $</Box> */}
                                        </Flex>
                                    </Box>
                                </Flex>
                            </Flex>
                            <form onSubmit={formik.handleSubmit} noValidate>
                                <Box mb={{ base: '24px', lg: '32px' }}>
                                    <Box mb={{ lg: '12px', base: '12px' }} fontSize={{ lg: '18px', base: '16px' }} fontWeight={700}>{intl.formatMessage({ id: 'NFT.SALE.PRICE' })}</Box>
                                    <Flex justify={'center'} align={'center'}>
                                        <Flex w={{ lg: 'calc(100% - 75px)', base: 'calc(100% - 75px)' }}>
                                            <Input
                                                {...formik.getFieldProps('price')}
                                                pl={'16px'} border={'none'} focusBorderColor='#29E9DD' h='48px' backgroundColor={'#FFFFFF0A'}
                                                name='price'
                                                type='number'
                                                placeholder={intl.formatMessage({ id: 'NFT.SALE.PRICE.PLACEHOLDER' })}
                                            />
                                        </Flex>
                                        <Box ml={{ lg: '16px', base: '16px' }} w={{ lg: '59px', base: '59px' }}>{USDT_CURRENCY}</Box>
                                    </Flex>
                                    {formik.touched.price && formik.errors.price && (
                                        <Box color={'#f1416c'}>
                                            <span role='alert'>{formik.errors.price}</span>
                                        </Box>
                                    )}
                                </Box>
                                <Box mb={{ base: '24px', lg: '32px' }} boxShadow="md" borderRadius="lg">
                                    <FormControl>
                                        <Box mb={{ lg: '12px', base: '12px' }} fontSize={{ lg: '18px', base: '16px' }} fontWeight={700}>{intl.formatMessage({ id: 'NFT.SALE.DURATION' })}</Box>
                                        <Flex flexWrap={'wrap'}>
                                            <Select mb={{ lg: 0, base: '12px' }} border={'none'} backgroundColor={'#FFFFFF0A'} focusBorderColor='#29E9DD' h='48px' mr={{ lg: '16px' }} flex={{ lg: '3' }} onChange={handleDurationChange} value={selectedDuration}>
                                                <option value="custom">{intl.formatMessage({ id: 'NFT.SALE.DURATION.CUSTOM' })}</option>
                                                <option value="1h">{intl.formatMessage({ id: 'NFT.SALE.DURATION.1.HOUURS' })}</option>
                                                <option value="6h">{intl.formatMessage({ id: 'NFT.SALE.DURATION.6.HOUURS' })}</option>
                                                <option value="1d">{intl.formatMessage({ id: 'NFT.SALE.DURATION.1.DAY' })}</option>
                                                <option value="3d">{intl.formatMessage({ id: 'NFT.SALE.DURATION.3.DAY' })}</option>
                                                <option value="7d">{intl.formatMessage({ id: 'NFT.SALE.DURATION.7.DAY' })}</option>
                                                <option value="1 month">{intl.formatMessage({ id: 'NFT.SALE.DURATION.1.MONTH' })}</option>
                                                <option value="3 month">{intl.formatMessage({ id: 'NFT.SALE.DURATION.3.MONTH' })}</option>
                                                <option value="1 year">{intl.formatMessage({ id: 'NFT.SALE.DURATION.1.YEAR' })}</option>
                                            </Select>
                                            <Box flex={{ lg: '7', base: '1' }}>
                                                <DatePicker
                                                    selected={endDate}
                                                    onChange={handleDateChange}
                                                    showTimeSelect
                                                    filterTime={filterPassedTime}
                                                    timeFormat="HH:mm"
                                                    timeIntervals={15}
                                                    timeCaption="Time"
                                                    dateFormat="MMM d, yyyy - h:mm aa"
                                                    minDate={new Date()}
                                                    locale="en-GB"
                                                    className="react-datepicker"
                                                    customInput={<CustomInput />}
                                                />
                                            </Box>
                                        </Flex>
                                    </FormControl>
                                </Box>
                                <Box mb={{ base: '24px', lg: '32px' }}>
                                    <Flex justify={'space-between'} >
                                        <Box color={'#808489'} fontWeight={700} fontSize={{ lg: '16px' }}>
                                            <Text mb={{ lg: '12px', base: '12px' }} >{intl.formatMessage({ id: 'NFT.SALE.PRICE.TOTAL' })}</Text>
                                            <Text>{intl.formatMessage({ id: 'NFT.SALE.FEE' })}</Text>
                                        </Box>
                                        <Box fontWeight={700} fontSize={{ lg: '16px' }}>
                                            <Text mb={{ lg: '12px', base: '12px' }} align={'end'}>{formik?.values?.price} {USDT_CURRENCY}</Text>
                                            <Text align={'end'}>{fee / 100} %</Text>
                                        </Box>
                                    </Flex>
                                    <Box height={'1px'} backgroundColor={'#27282A'} my={{ lg: '16px', base: '16px' }}></Box>
                                    <Flex justify={'space-between'}>
                                        <Text mb={{ lg: '12px' }} fontSize={{ lg: '18px' }} fontWeight={700}>{intl.formatMessage({ id: 'NFT.SALE.TOTAL.EARNINGS' })}</Text>
                                        <Text align={'end'} mb={{ lg: '12px' }} fontSize={{ lg: '18px' }} fontWeight={700}>{formik?.values?.price as number * (1 - fee / 10000)} {USDT_CURRENCY}</Text>
                                    </Flex>
                                </Box>
                            </form>
                            <Box mb={{ base: '24px', lg: '32px' }}>
                                <Button
                                    onClick={formik?.isSubmitting ? () => { } : handleLogicListingNft}
                                    w={'100%'}
                                    type='submit'
                                    as={'button'}
                                    color='#FFFFFF'
                                    cursor={'pointer'}
                                    rounded="6px"
                                    fontSize={{ base: '18px', lg: '18px' }}
                                    fontWeight='600'
                                    transition='all 0.2s cubic-bezier(.08,.52,.52,1)'
                                    bg={'linear-gradient(90deg, #29E9DD, #F99FAB)'}
                                    p={'2px'}
                                    height={{ base: '48px', lg: '48px' }}
                                    _hover={{ bg: 'linear-gradient(90deg, #F99FAB, #29E9DD)', transition: 'all 0.2s cubic-bezier(.08,.52,.52,1)' }}

                                    _active={{
                                        bg: '#dddfe2',
                                        transform: 'scale(0.98)',
                                        borderColor: '#bec3c9',
                                    }}
                                    disabled={!formik?.values?.price || formik?.isSubmitting}
                                    opacity={(!formik?.values?.price || formik?.isSubmitting) ? '30%' : ''}
                                >
                                    <Box display={'flex'} justifyContent={'center'} alignItems={'center'} w={'100%'} h={'100%'} bg="#131619" rounded="4px">
                                        <Box w={'100%'} color={'#FFFFFF'} style={{ textWrap: 'nowrap' }} display='flex' alignItems={'center'} justifyContent={'center'} px={{ base: '12px', lg: '24px' }} h={'100%'} textAlign={'center'} bgColor={'#FFFFFF0A'}>
                                            {intl.formatMessage({ id: 'NFT.SALE.BUTTON.LISTING' })}
                                            {formik?.isSubmitting && <CircularProgress pl={'10px'} size={'20px'} isIndeterminate color='green.700' />}
                                        </Box>
                                    </Box>
                                </Button>
                            </Box>
                        </ModalBody>
                    </ModalContent>
                </Modal>}
            {openModalApproveListing && <ApproveListingModal network={network as string} priceUsd={priceUsd} formik={formik} nftInformation={nftInformation} openModalApproveListing={openModalApproveListing} onCloseModalApproveListing={onCloseModalApproveListing} />}
            {openModalViewListing && <ViewListingModal nftInformation={nftInformation} openModalViewListing={openModalViewListing} onCloseModalViewListing={onCloseModalViewListing} />}
        </>
    )
}
